/**
 * <pga-swedish-players> — Swedish player directory on the PGA Tour page.
 *
 * Data: GET data-endpoint (the merchant's Cloudflare Worker /players) → { data: Player[] }.
 * The Worker holds the API key; this file never sends credentials and never calls the upstream API.
 * OWGR values are shown exactly as supplied; a missing value is "unranked", never zero.
 * Every API string is written with textContent into markup cloned from <template data-players-tpl>.
 *
 * Commands (buttons with commandfor="<this id>"):
 *   --player        data-id + data-scope → open/close that player's profile panel
 *   --close-player  data-scope → close the open panel and return focus to its card
 *   --toggle-all    expand/collapse the full player list
 *   --retry         refetch after an error
 */

const FETCH_TIMEOUT_MS = 12000;
const FEATURED_COUNT = 4;
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

class PgaSwedishPlayers extends HTMLElement {
  #ac;
  #t = {};
  #locale = 'sv-SE';
  #players = [];
  #featured = [];
  #open = { featured: null, all: null };
  #allOpen = false;
  #allRendered = false;
  #countryNames = null;

  connectedCallback() {
    this.#ac = new AbortController();
    if (!this.id) this.id = 'pga-swedish-players';
    this.#locale = document.documentElement.lang || 'sv-SE';
    this.#readConfig();
    this.addEventListener('command', (e) => this.#onCommand(e), { signal: this.#ac.signal });
    this.#load();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // ---------- setup ----------

  #readConfig() {
    const node = this.querySelector('script[data-players-config]');
    if (!node) return;
    try {
      const config = JSON.parse(node.textContent);
      this.#t = config.t || {};
    } catch {
      this.#t = {};
    }
  }

  #q(name) {
    return this.querySelector(`[data-players="${name}"]`);
  }

  #tpl(name) {
    const tpl = this.querySelector(`template[data-players-tpl="${name}"]`);
    return tpl ? tpl.content.firstElementChild.cloneNode(true) : document.createElement('div');
  }

  // ---------- data ----------

  async #load() {
    this.#renderLoading();
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return this.#renderMessage(this.#t.error, true);

    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), FETCH_TIMEOUT_MS);
    this.#ac.signal.addEventListener('abort', () => timeout.abort(), { once: true });

    try {
      const res = await fetch(endpoint, { signal: timeout.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !Array.isArray(json.data)) throw new Error('Unexpected payload');

      const valid = json.data.filter((p) => p && nameOf(p));
      const ranked = valid.filter((p) => rankOf(p) !== null).sort((a, b) => rankOf(a) - rankOf(b));
      const unranked = valid.filter((p) => rankOf(p) === null);
      this.#players = [...ranked, ...unranked];
      this.#featured = ranked.slice(0, FEATURED_COUNT);

      if (!this.#players.length) return this.#renderMessage(this.#t.empty, false);
      this.#render(ranked, unranked);
    } catch (err) {
      if (this.#ac.signal.aborted) return;
      console.warn('[pga-swedish-players]', err);
      this.#renderMessage(this.#t.error, true);
    } finally {
      clearTimeout(timer);
    }
  }

  // ---------- render ----------

  #render(ranked, unranked) {
    this.#open = { featured: null, all: null };
    this.#allRendered = false;
    this.#ranked = ranked;
    this.#unranked = unranked;

    const status = this.#q('status');
    status?.replaceChildren();
    if (status) status.hidden = true;

    const grid = this.#q('featured');
    if (grid) {
      grid.removeAttribute('aria-busy');
      grid.replaceChildren(...this.#featured.map((p, i) => this.#card(p, i)));
      grid.hidden = !this.#featured.length;
    }
    if (!this.#featured.length && status) {
      status.replaceChildren(this.#message(this.#t.no_ranked, false));
      status.hidden = false;
    }

    const detail = this.#q('detail');
    if (detail) {
      detail.hidden = true;
      detail.replaceChildren();
    }

    const toggle = this.#q('toggle');
    if (toggle) toggle.hidden = this.#players.length <= this.#featured.length;

    // Re-apply an expanded list after a retry.
    if (this.#allOpen) this.#setAllOpen(true, false);
  }

  #ranked = [];
  #unranked = [];

  #card(p, index = 0) {
    const node = this.#tpl('card');
    const trigger = node.querySelector('[data-slot="trigger"]');
    // Position-based accent colour (block settings), cycling through four.
    const slot = index % 4;
    trigger.style.setProperty('--player-accent', `var(--player-accent-${slot + 1})`);
    // White arrow on the dark first accent, dark arrow on the lighter sand/sage/olive buttons.
    trigger.style.setProperty('--player-ink', slot === 0 ? '#fff' : 'var(--color-on-surface)');
    this.#wireTrigger(trigger, p, 'featured', this.#q('detail')?.id);
    this.#flag(node, p);
    setText(node, 'rank', rankOf(p) ?? this.#t.unranked);
    setText(node, 'name', nameOf(p));
    const meta = node.querySelector('[data-slot="meta"]');
    const debut = clean(p.turned_pro);
    const age = ageOf(p.birth_date);
    if (debut) meta.appendChild(span(fill(this.#t.turned_pro, { year: debut })));
    if (age !== null) meta.appendChild(span(fill(this.#t.age, { age })));
    return node;
  }

  #renderAll() {
    const list = this.#q('all');
    if (!list) return;
    const groups = [];
    if (this.#ranked.length) groups.push(this.#group(this.#t.group_ranked, this.#ranked));
    if (this.#unranked.length) groups.push(this.#group(this.#t.group_unranked, this.#unranked));
    list.replaceChildren(...groups);
    this.#allRendered = true;
  }

  #group(label, players) {
    const node = this.#tpl('group');
    setText(node, 'label', label);
    const ul = node.querySelector('[data-slot="list"]');
    players.forEach((p) => ul.appendChild(this.#row(p)));
    return node;
  }

  #row(p) {
    const node = this.#tpl('row');
    const trigger = node.querySelector('[data-slot="trigger"]');
    const panel = node.querySelector('[data-slot="panel"]');
    panel.id = `${this.id}-row-${String(p.id ?? nameOf(p)).replace(/[^\w-]/g, '')}`;
    this.#wireTrigger(trigger, p, 'all', panel.id);
    this.#flag(node, p);
    const rank = node.querySelector('[data-slot="rank"]');
    if (rankOf(p) !== null) {
      rank.textContent = rankOf(p);
    } else {
      rank.textContent = this.#t.unranked;
      rank.setAttribute('data-unranked', '');
    }
    setText(node, 'name', nameOf(p));
    const debut = clean(p.turned_pro);
    const age = ageOf(p.birth_date);
    setText(node, 'debut', debut ? fill(this.#t.turned_pro, { year: debut }) : '');
    setText(node, 'age', age !== null ? fill(this.#t.age, { age }) : '');
    return node;
  }

  #wireTrigger(trigger, p, scope, controls) {
    trigger.setAttribute('commandfor', this.id);
    trigger.dataset.id = keyOf(p);
    trigger.dataset.scope = scope;
    if (controls) trigger.setAttribute('aria-controls', controls);
  }

  #flag(node, p) {
    if (clean(p.country_code).toUpperCase() !== 'SWE') node.querySelectorAll('[data-slot="flag"]').forEach((f) => f.remove());
  }

  #detail(p, scope) {
    const node = this.#tpl('detail');
    const name = nameOf(p);
    node.setAttribute('aria-label', name);
    this.#flag(node, p);
    setText(node, 'nationality', this.#country(p.country));
    setText(node, 'name', name);
    setText(node, 'rank', rankOf(p) ?? this.#t.unranked);

    const close = node.querySelector('[data-slot="close"]');
    close.setAttribute('commandfor', this.id);
    close.dataset.scope = scope;

    const facts = node.querySelector('[data-slot="facts"]');
    const add = (label, value) => {
      if (!value) return;
      const fact = this.#tpl('fact');
      fact.querySelector('dt').textContent = label;
      fact.querySelector('dd').textContent = value;
      facts.appendChild(fact);
    };

    const first = clean(p.first_name);
    const last = clean(p.last_name);
    add(this.#t.full_name, first && last ? `${first} ${last}` : '');
    add(this.#t.nationality, this.#country(p.country));
    add(this.#t.owgr, rankOf(p) !== null ? String(rankOf(p)) : this.#t.unranked);
    add(this.#t.birth_date, this.#birthDate(p.birth_date));
    add(this.#t.birthplace, this.#place(p.birthplace_city, p.birthplace_state, p.birthplace_country));
    add(this.#t.debut, clean(p.turned_pro));
    add(this.#t.school, clean(p.school));
    add(this.#t.residence, this.#place(p.residence_city, p.residence_state, p.residence_country));
    return node;
  }

  // ---------- states ----------

  #renderLoading() {
    const grid = this.#q('featured');
    if (grid) {
      grid.hidden = false;
      grid.setAttribute('aria-busy', 'true');
      const items = Array.from({ length: FEATURED_COUNT }, () => this.#tpl('skeleton'));
      const label = document.createElement('li');
      label.className = 'sr-only';
      label.textContent = this.#t.loading || '';
      grid.replaceChildren(label, ...items);
    }
    const status = this.#q('status');
    if (status) {
      status.hidden = true;
      status.replaceChildren();
    }
    const toggle = this.#q('toggle');
    if (toggle) toggle.hidden = true;
  }

  #renderMessage(text, retry) {
    const grid = this.#q('featured');
    if (grid) {
      grid.removeAttribute('aria-busy');
      grid.replaceChildren();
      grid.hidden = true;
    }
    const detail = this.#q('detail');
    if (detail) {
      detail.hidden = true;
      detail.replaceChildren();
    }
    const all = this.#q('all');
    if (all) {
      all.hidden = true;
      all.replaceChildren();
    }
    this.#allRendered = false;
    const toggle = this.#q('toggle');
    if (toggle) {
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }
    const status = this.#q('status');
    if (status) {
      status.replaceChildren(this.#message(text, retry));
      status.hidden = false;
    }
  }

  #message(text, retry) {
    const node = this.#tpl('message');
    setText(node, 'text', text);
    const button = node.querySelector('[data-slot="retry"]');
    if (retry) {
      node.setAttribute('role', 'alert');
      button.setAttribute('commandfor', this.id);
    } else {
      button.remove();
    }
    return node;
  }

  // ---------- commands ----------

  #onCommand(e) {
    const source = e.source;
    switch (e.command) {
      case '--player':
        return this.#togglePlayer(source);
      case '--close-player':
        return this.#closePlayer(source?.dataset.scope, true);
      case '--toggle-all':
        return this.#setAllOpen(!this.#allOpen, true);
      case '--retry':
        return this.#load();
    }
  }

  #togglePlayer(trigger) {
    if (!trigger) return;
    const scope = trigger.dataset.scope === 'all' ? 'all' : 'featured';
    const id = trigger.dataset.id;
    if (this.#open[scope] === id) return this.#closePlayer(scope, false);

    const player = this.#players.find((p) => keyOf(p) === id);
    if (!player) return;
    this.#closePlayer(scope, false);

    const panel = scope === 'all' ? trigger.parentElement.querySelector('[data-slot="panel"]') : this.#q('detail');
    if (!panel) return;
    panel.replaceChildren(this.#detail(player, scope));
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#open[scope] = id;

    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    requestAnimationFrame(() => panel.scrollIntoView({ block: 'nearest', behavior }));
  }

  #closePlayer(scope, restoreFocus) {
    const id = this.#open[scope];
    if (!id) return;
    const trigger = this.querySelector(`[data-slot="trigger"][data-scope="${scope}"][data-id="${CSS.escape(id)}"]`);
    const panel = scope === 'all' ? trigger?.parentElement.querySelector('[data-slot="panel"]') : this.#q('detail');
    if (panel) {
      panel.hidden = true;
      panel.replaceChildren();
    }
    trigger?.setAttribute('aria-expanded', 'false');
    this.#open[scope] = null;
    if (restoreFocus) trigger?.focus();
  }

  #setAllOpen(open, userAction) {
    const list = this.#q('all');
    const toggle = this.#q('toggle');
    if (!list) return;
    if (open && !this.#allRendered) this.#renderAll();
    if (!open) this.#closePlayer('all', false);
    this.#allOpen = open;
    list.hidden = !open;
    toggle?.setAttribute('aria-expanded', String(open));
    if (!open && userAction) {
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      toggle?.scrollIntoView({ block: 'nearest', behavior });
    }
  }

  // ---------- formatting ----------

  #birthDate(raw) {
    const date = parseBirthDate(raw);
    if (!date) return clean(raw);
    let text = clean(raw);
    try {
      text = new Intl.DateTimeFormat(this.#locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
    } catch {
      /* keep the API string */
    }
    const age = ageOf(raw);
    return age !== null ? `${text} (${fill(this.#t.age, { age })})` : text;
  }

  #place(city, state, country) {
    return [clean(city), clean(state), this.#country(country)].filter(Boolean).join(', ');
  }

  // Localises an English country name from the API (e.g. "Sweden" → "Sverige"); unknown names pass through unchanged.
  #country(name) {
    const value = clean(name);
    if (!value) return '';
    try {
      if (!this.#countryNames) {
        const en = new Intl.DisplayNames(['en'], { type: 'region' });
        this.#countryNames = new Map();
        for (let a = 65; a <= 90; a++) {
          for (let b = 65; b <= 90; b++) {
            const code = String.fromCharCode(a, b);
            const label = en.of(code);
            if (label && label !== code) this.#countryNames.set(label.toLowerCase(), code);
          }
        }
      }
      const code = this.#countryNames.get(value.toLowerCase());
      return code ? new Intl.DisplayNames([this.#locale], { type: 'region' }).of(code) || value : value;
    } catch {
      return value;
    }
  }
}

// ---------- helpers (pure) ----------

function clean(value) {
  if (value == null) return '';
  const s = String(value).trim();
  return s && s.toLowerCase() !== 'null' ? s : '';
}

function nameOf(p) {
  return clean(p.display_name) || [clean(p.first_name), clean(p.last_name)].filter(Boolean).join(' ');
}

function keyOf(p) {
  return String(p.id ?? nameOf(p));
}

// A ranking only counts when the API supplies a positive whole number; null, 0 or text are "unranked".
function rankOf(p) {
  const n = typeof p.owgr === 'string' ? Number(p.owgr.trim()) : p.owgr;
  return Number.isInteger(n) && n > 0 ? n : null;
}

// Accepts the API's "Oct 31, 1999" format only; anything else is treated as not reliably available.
function parseBirthDate(raw) {
  const m = /^([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),\s*(\d{4})$/.exec(clean(raw));
  if (!m) return null;
  const month = MONTHS[m[1].toLowerCase()];
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (month === undefined) return null;
  const date = new Date(Date.UTC(year, month, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) return null;
  return date;
}

function ageOf(raw) {
  const date = parseBirthDate(raw);
  if (!date) return null;
  const now = new Date();
  let age = now.getFullYear() - date.getUTCFullYear();
  const beforeBirthday =
    now.getMonth() < date.getUTCMonth() || (now.getMonth() === date.getUTCMonth() && now.getDate() < date.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age >= 14 && age <= 100 ? age : null;
}

function fill(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (_, key) => (values[key] ?? ''));
}

function setText(root, slot, text) {
  const node = root.querySelector(`[data-slot="${slot}"]`);
  if (node) node.textContent = text == null ? '' : String(text);
}

function span(text) {
  const node = document.createElement('span');
  node.textContent = text;
  return node;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

if (!customElements.get('pga-swedish-players')) {
  customElements.define('pga-swedish-players', PgaSwedishPlayers);
}
